import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './ReviewApplicationPage.css';

const mapStatus = (status) => {
  switch (status) {
    case 'submitted':
      return 'Submitted';
    case 'reviewed':
      return 'In Review';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Not Approved';
    case 'needsMoreInfo':
      return 'Needs More Info';
    case 'waitlisted':
      return 'Waitlisted';
    default:
      return status;
  }
};

const looksLikeAttachmentName = (value) => (
  typeof value === 'string' && /\.(pdf|docx?|xlsx?|png|jpe?g)$/i.test(value)
);

const ReviewHealthApplicationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useContext(AuthContext);
  const [application, setApplication] = useState(null);
  const [programDetails, setProgramDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [applicationFiles, setApplicationFiles] = useState({});
  const [reviewNotes, setReviewNotes] = useState('');

  const isApplicantView = searchParams.get('view') === 'applicant';
  const hasAdminAccess = currentUser && !isApplicantView && currentUser.health;

  const fetchApplication = useCallback(async () => {
    try {
      const response = await api.get(`/api/health-program-applications/${id}`);
      setApplication(response.data.application);
      setReviewNotes(response.data.application.reviewNotes || '');
      
      // Extract healthProgramId - handle both object and string cases
      const healthProgramId = response.data.application.healthProgramId?._id || 
                              response.data.application.healthProgramId;
      
      if (healthProgramId) {
        try {
          const programResponse = await api.get(`/api/health-programs/${healthProgramId}`);
          setProgramDetails(programResponse.data.program);
        } catch (programErr) {
          console.error("Error fetching program:", programErr);
        }
      }

      try {
        if (response.data.application._id) {
          const filesResponse = await api.get(`/api/files/application/${response.data.application._id}`);
          const filesMap = {};
          filesResponse.data.forEach(file => {
            filesMap[file.questionKey] = file;
          });
          setApplicationFiles(filesMap);
        }
      } catch (filesErr) {
        console.error("Error fetching application files:", filesErr);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching application:', err);
      setError(err.response?.data?.message || 'Error fetching application');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchApplication();
    }
  }, [id, fetchApplication]);

  const updateApplicationStatus = async (newStatus) => {
    setStatusLoading(true);
    try {
      await api.patch(`/api/health-program-applications/${id}/status`, { 
        status: newStatus,
        reviewNotes
      });
      setApplication({ ...application, status: newStatus, reviewNotes });
      setMessage(`Application status updated to ${mapStatus(newStatus)}`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating application status');
    } finally {
      setStatusLoading(false);
    }
  };

  const getQuestionText = (sectionIdx, questionIdx) => {
    if (!programDetails || !programDetails.sections) return "Question text not available";
    
    try {
      const section = programDetails.sections[parseInt(sectionIdx)];
      if (!section || !section.questions) return "Question not found";
      
      const question = section.questions[parseInt(questionIdx)];
      return question ? question.label : "Question not found";
    } catch (err) {
      console.error("Error finding question:", err);
      return "Question text not available";
    }
  };

  const getSectionName = (sectionIdx) => {
    if (!programDetails || !programDetails.sections) return `Section ${Number(sectionIdx) + 1}`;
    
    try {
      const section = programDetails.sections[parseInt(sectionIdx)];
      return section ? section.title : `Section ${Number(sectionIdx) + 1}`;
    } catch (err) {
      return `Section ${Number(sectionIdx) + 1}`;
    }
  };

  const renderAnswer = (item) => {
    const { answer, key } = item;
    
    if (applicationFiles[key]) {
      const file = applicationFiles[key];
      const authToken = localStorage.getItem('authToken');
      return (
        <div className="file-answer">
          <span>{file.originalname}</span>
          <a 
            href={`${api.defaults.baseURL}/api/files/${file._id}?token=${encodeURIComponent(authToken)}`}
            className="download-button"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </a>
        </div>
      );
    }

    if (looksLikeAttachmentName(answer)) {
      return (
        <div className="file-answer file-answer-missing">
          <span>{answer}</span>
          <span className="missing-file-note">
            {hasAdminAccess ? 'Attachment record missing' : 'Attachment unavailable'}
          </span>
        </div>
      );
    }
    
    if (!Array.isArray(answer)) {
      return answer !== "" && answer !== false ? 
        (typeof answer === 'boolean' ? (answer ? 'Yes' : 'No') : answer) : 
        <em>No answer</em>;
    }
    
    if (answer.length === 0) {
      return <em>No answer</em>;
    }
    
    return (
      <ul className="multi-select-answer-list">
        {answer.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  };

  const renderAttachmentsPanel = () => {
    const fileList = Object.values(applicationFiles);
    if (fileList.length === 0) return null;
    const authToken = localStorage.getItem('authToken');

    const formatSize = (bytes) => {
      if (!bytes) return '';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${sizes[i]}`;
    };

    const getFileIcon = (mimetype) => {
      if (!mimetype) return '📄';
      if (mimetype.includes('pdf')) return '📄';
      if (mimetype.includes('image')) return '🖼️';
      if (mimetype.includes('word') || mimetype.includes('document')) return '📝';
      if (mimetype.includes('sheet') || mimetype.includes('excel')) return '📈';
      return '📁';
    };

    return (
      <div className="attachments-panel">
        <h3>Attachments ({fileList.length})</h3>
        <div className="attachments-grid">
          {fileList.map((file) => (
            <div key={file._id} className="attachment-card">
              <div className="attachment-icon">{getFileIcon(file.mimetype)}</div>
              <div className="attachment-info">
                <div className="attachment-name" title={file.originalname}>
                  {file.originalname}
                </div>
                <div className="attachment-meta">
                  {formatSize(file.size)}
                  {file.mimetype && (
                    <span className="attachment-type">
                      {file.mimetype.split('/').pop().toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              <div className="attachment-actions">
                <a
                  href={`${api.defaults.baseURL}/api/files/${file._id}?token=${encodeURIComponent(authToken)}`}
                  className="attach-btn attach-btn-download"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Download"
                >
                  ↓ Download
                </a>
                {file.mimetype && (file.mimetype.includes('pdf') || file.mimetype.includes('image')) && (
                  <a
                    href={`${api.defaults.baseURL}/api/files/${file._id}?token=${encodeURIComponent(authToken)}&inline=true`}
                    className="attach-btn attach-btn-view"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View in browser"
                  >
                    🔍 View
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAnswers = () => {
    if (!application.answers) return <p>No answers provided</p>;

    const groups = {};
    Object.keys(application.answers).forEach(key => {
      const [sectionIdx, questionIdx] = key.split('-');
      if (!groups[sectionIdx]) {
        groups[sectionIdx] = [];
      }
      groups[sectionIdx].push({ 
        questionIdx, 
        answer: application.answers[key], 
        key,
        questionText: getQuestionText(sectionIdx, questionIdx)
      });
    });
    
    return Object.entries(groups).map(([section, qList]) => (
      <div key={section} className="answer-section">
        <h3>{getSectionName(section)}</h3>
        <ul className="answer-list">
          {qList.map((item, idx) => (
            <li key={idx} className="answer-item">
              <div className="question-label">
                <strong>{item.questionText}</strong>
              </div>
              <div className="answer-content">
                {renderAnswer(item)}
              </div>
            </li>
          ))}
        </ul>
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading application details...</p>
      </div>
    );
  }

  if (error && !application) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="loading-container">
        <p>Loading application details...</p>
      </div>
    );
  }

  return (
    <div className="review-application-container">
      <div className="page-header">
        <h1>{hasAdminAccess ? "Review Health Program Application" : "Application Details"}</h1>
        <button onClick={() => navigate(-1)} className="btn-back">
          ← Back
        </button>
      </div>
      
      {message && <div className="status-message success-message">{message}</div>}
      
      <div className="application-card">
        <div className="application-header">
          <h2>{application.programTitle}</h2>
          <div className={`status-badge status-${application.status?.toLowerCase()}`}>
            {mapStatus(application.status)}
          </div>
        </div>

        <div className="applicant-details">
          <h3>Application Information</h3>
          <div className="detail-row">
            <span className="detail-label">Applicant Name:</span>
            <span className="detail-value">{application.applicantName}</span>
          </div>
          {hasAdminAccess && application.user && (
            <>
              <div className="detail-row">
                <span className="detail-label">Applicant Email:</span>
                <span className="detail-value">{application.user.email}</span>
              </div>
              {application.user.phone && (
                <div className="detail-row">
                  <span className="detail-label">Phone:</span>
                  <span className="detail-value">{application.user.phone}</span>
                </div>
              )}
            </>
          )}
          <div className="detail-row">
            <span className="detail-label">Program Type:</span>
            <span className="detail-value">{application.programType}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Date Submitted:</span>
            <span className="detail-value">{new Date(application.dateSubmitted).toLocaleString()}</span>
          </div>
          {application.reviewedBy && (
            <>
              <div className="detail-row">
                <span className="detail-label">Reviewed By:</span>
                <span className="detail-value">
                  {application.reviewedBy.firstName} {application.reviewedBy.lastName}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Reviewed At:</span>
                <span className="detail-value">{new Date(application.reviewedAt).toLocaleString()}</span>
              </div>
            </>
          )}
        </div>
        
        {hasAdminAccess && (
          <>
            <div className="status-action-container">
              <h3>Application Status</h3>
              <div className="status-controls">
                <button 
                  className={`status-btn ${application.status === 'submitted' ? 'active' : ''}`}
                  onClick={() => updateApplicationStatus('submitted')}
                  disabled={statusLoading}
                >
                  Submitted
                </button>
                <button 
                  className={`status-btn ${application.status === 'reviewed' ? 'active' : ''}`}
                  onClick={() => updateApplicationStatus('reviewed')}
                  disabled={statusLoading}
                >
                  In Review
                </button>
                <button 
                  className={`status-btn approve-btn ${application.status === 'approved' ? 'active' : ''}`}
                  onClick={() => updateApplicationStatus('approved')}
                  disabled={statusLoading}
                >
                  Approve
                </button>
                <button 
                  className={`status-btn reject-btn ${application.status === 'rejected' ? 'active' : ''}`}
                  onClick={() => updateApplicationStatus('rejected')}
                  disabled={statusLoading}
                >
                  Not Approved
                </button>
                <button 
                  className={`status-btn ${application.status === 'needsMoreInfo' ? 'active' : ''}`}
                  onClick={() => updateApplicationStatus('needsMoreInfo')}
                  disabled={statusLoading}
                >
                  Needs More Info
                </button>
                <button 
                  className={`status-btn ${application.status === 'waitlisted' ? 'active' : ''}`}
                  onClick={() => updateApplicationStatus('waitlisted')}
                  disabled={statusLoading}
                >
                  Waitlist
                </button>
              </div>
            </div>

            <div className="review-notes-container">
              <h3>Review Notes</h3>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add internal notes about this application..."
                rows={4}
                className="review-notes-textarea"
              />
              <button 
                onClick={() => updateApplicationStatus(application.status)}
                className="btn-save-notes"
                disabled={statusLoading}
              >
                Save Notes
              </button>
            </div>
          </>
        )}

        {application.reviewNotes && !hasAdminAccess && (
          <div className="applicant-notes">
            <h3>Reviewer Notes</h3>
            <p>{application.reviewNotes}</p>
          </div>
        )}
        
        <div className="answers-container">
          <h3>Application Responses</h3>
          {renderAttachmentsPanel()}
          {renderAnswers()}
        </div>

        <div className="action-buttons">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate(hasAdminAccess
              ? (application?.portal === 'education' ? '/grants?category=education' : '/staff-health-programs')
              : '/dashboard'
            )}
          >
            {hasAdminAccess ? 'Back to Programs' : 'Back to Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewHealthApplicationPage;
